import { addDays, endOfWeek, format, startOfWeek } from 'date-fns';
import { it } from 'date-fns/locale';
import React, { useEffect, useMemo, useState } from 'react';
import { searchMunicipalities } from '../utils/italianMunicipalities';
import { Autocomplete, AutocompleteOption } from './ui/autocomplete';
import { Button } from './ui/button';

const timeSlots = [
    { label: "Prima mattina", time: "07:00 - 09:00" },
    { label: "Tarda mattina", time: "09:00 - 12:00" },
    { label: "Primo pomeriggio", time: "12:00 - 16:00" }
];

type Step = 'comune' | 'week' | 'time' | 'summary' | 'success';

interface BloodTestBannerProps {
  onExpandedChange?: (isExpanded: boolean) => void;
}

const BloodTestBanner: React.FC<BloodTestBannerProps> = ({ onExpandedChange }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [showComuneSelection, setShowComuneSelection] = useState(false);
    const [currentStep, setCurrentStep] = useState<Step>('comune');
    const [selectedComune, setSelectedComune] = useState<string>("");
    const [selectedWeek, setSelectedWeek] = useState<string>("");
    const [selectedTime, setSelectedTime] = useState<string>("");
    const [requestSent, setRequestSent] = useState(false);

    // Convert municipalities to autocomplete options
    const comuneOptions = useMemo((): AutocompleteOption[] => {
        return searchMunicipalities(selectedComune).map(municipality => ({
            value: municipality.fullName,
            label: municipality.fullName,
            municipality: municipality
        }));
    }, [selectedComune]);

    // Notify parent when expanded state changes
    useEffect(() => {
        onExpandedChange?.(isExpanded || showComuneSelection);
    }, [isExpanded, showComuneSelection, onExpandedChange]);

    const getWeekRange = (isNextWeek: boolean) => {
        const today = new Date();
        const startDate = startOfWeek(isNextWeek ? addDays(today, 7) : today, { locale: it });
        const endDate = endOfWeek(isNextWeek ? addDays(today, 7) : today, { locale: it });
        return `${format(startDate, 'dd.MM', { locale: it })}-${format(endDate, 'dd.MM', { locale: it })}`;
    };

    const toggleExpand = () => {
        setIsExpanded(!isExpanded);
        if (!isExpanded) {
            setShowComuneSelection(false);
            setCurrentStep('comune');
        }
    };

    const handleRequestClick = () => {
        setShowComuneSelection(true);
    };

    const handleComuneConfirm = () => {
        setCurrentStep('week');
    };

    const handleWeekSelect = (week: string) => {
        setSelectedWeek(week);
        setCurrentStep('time');
    };

    const handleTimeSelect = (time: string) => {
        setSelectedTime(time);
        setCurrentStep('summary');
    };

    const handleModify = (step: Step) => {
        setCurrentStep(step);
    };

    const handleFinalConfirm = () => {
        // TODO: Handle API call to save the request
        setCurrentStep('success');
        setRequestSent(true);
    };

    const handleClose = () => {
        setIsExpanded(false);
        setShowComuneSelection(false);
    };

    const handleComuneSelect = (option: AutocompleteOption) => {
        setSelectedComune(option.label);
    };

    const renderSelectionSummary = () => {
        const selections = [];
        
        if (selectedComune) {
            selections.push(
                <div key="comune" className="flex items-center justify-between text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                        <span>📍</span>
                        <span className='italic'>{selectedComune}</span>
                    </div>
                    <Button 
                        variant="white" 
                        className="text-sm text-primary hover:text-primary/80 normal-case"
                        onClick={() => handleModify('comune')}
                    >
                        Modifica
                    </Button>
                </div>
            );
        }
        
        if (selectedWeek) {
            selections.push(
                <div key="week" className="flex items-center justify-between text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                        <span>🗓️</span>
                        <span className='italic'>{selectedWeek === 'this' ? 'Questa settimana' : 'Prossima settimana'} | {getWeekRange(selectedWeek === 'next')}</span>
                    </div>
                    <Button 
                        variant="white" 
                        className="text-sm text-primary hover:text-primary/80 normal-case"
                        onClick={() => handleModify('week')}
                    >
                        Modifica
                    </Button>
                </div>
            );
        }
        
        if (selectedTime) {
            selections.push(
                <div key="time" className="flex items-center justify-between text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                        <span>🕥</span>
                        <span className='italic'>{selectedTime}</span>
                    </div>
                    <Button 
                        variant="white" 
                        className="text-sm text-primary hover:text-primary/80 normal-case"
                        onClick={() => handleModify('time')}
                    >
                        Modifica
                    </Button>
                </div>
            );
        }

        if (selections.length === 0) return null;

        return (
            <div className="flex flex-col gap-2 py-3">
                <div className="flex flex-col gap-1">
                    {selections}
                </div>
            </div>
        );
    };

    const renderBookingOverview = () => {
        return (
            <div className="flex flex-col gap-4">
                <div className="text-base font-semibold">
                    La tua prenotazione è per domani!
                </div>
                <div className="flex flex-col gap-2 p-4 rounded-lg border border-border">
                    <div className="flex items-center gap-2">
                        <span>🗓️</span>
                        <span className="font-medium">CH | Milano De Amicis</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                        <span>15 mag</span>
                        <span>•</span>
                        <span>07:30</span>
                    </div>
                </div>
            </div>
        );
    };

    const renderStep = () => {
        switch (currentStep) {
            case 'comune':
                return (
                    <div className="flex flex-col gap-4">
                        <div className="text-base font-semibold">
                            Inserisci il tuo comune
                        </div>
                        <Autocomplete
                            options={comuneOptions}
                            value={selectedComune}
                            onChange={setSelectedComune}
                            onSelect={handleComuneSelect}
                            placeholder="Es. Milano, Roma, Napoli..."
                            className="w-full"
                        />
                        <Button 
                            size={'form'} 
                            className='normal-case text-base'
                            onClick={handleComuneConfirm}
                            disabled={!selectedComune.trim()}
                        >
                            Conferma
                        </Button>
                    </div>
                );
            case 'week':
                return (
                    <div className="flex flex-col gap-4">
                        <div className="text-base font-semibold">
                            Scegli quando potresti effettuarli
                        </div>
                        {renderSelectionSummary()}
                        <div className="grid grid-cols-1 gap-2">
                            <button
                                onClick={() => handleWeekSelect('this')}
                                className={`flex items-center gap-2 p-4 rounded-lg border transition-all ${
                                    selectedWeek === 'this' 
                                    ? 'border-primary bg-primary/5' 
                                    : 'border-border hover:border-primary/50'
                                }`}
                            >
                                <span>🗓️</span>
                                <span className="flex-1 text-left">
                                    Questa settimana | {getWeekRange(false)}
                                </span>
                            </button>
                            <button
                                onClick={() => handleWeekSelect('next')}
                                className={`flex items-center gap-2 p-4 rounded-lg border transition-all ${
                                    selectedWeek === 'next' 
                                    ? 'border-primary bg-primary/5' 
                                    : 'border-border hover:border-primary/50'
                                }`}
                            >
                                <span>🗓️</span>
                                <span className="flex-1 text-left">
                                    Prossima settimana | {getWeekRange(true)}
                                </span>
                            </button>
                        </div>
                    </div>
                );
            case 'time':
                return (
                    <div className="flex flex-col gap-4">
                        <div className="text-base font-semibold">
                            Scegli l'orario
                        </div>
                        {renderSelectionSummary()}
                        <div className="grid grid-cols-1 gap-2">
                            {timeSlots.map((slot) => (
                                <button
                                    key={slot.label}
                                    onClick={() => handleTimeSelect(slot.label)}
                                    className={`flex items-center gap-2 p-4 rounded-lg border transition-all ${
                                        selectedTime === slot.label 
                                        ? 'border-primary bg-primary/5' 
                                        : 'border-border hover:border-primary/50'
                                    }`}
                                >
                                    <span>🕥</span>
                                    <span className="flex-1 text-left">
                                        {slot.label} | {slot.time}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>
                );
            case 'summary':
                return (
                    <div className="flex flex-col gap-4">
                        <div className="text-base font-semibold">
                            Conferma le tue preferenze
                        </div>
                        {renderSelectionSummary()}
                        <Button 
                            size={'form'} 
                            className='normal-case text-base'
                            onClick={handleFinalConfirm}
                        >
                            Conferma richiesta disponibilità
                        </Button>
                    </div>
                );
            case 'success':
                return (
                    <div className="flex flex-col gap-4">
                        <div className="text-center">
                            <div className="text-lg font-semibold mb-2">Tutto fatto!</div>
                            <div className="text-sm">
                                Riceverai a breve per mail le migliori opzioni tra le nostre cliniche secondo le tue preferenze
                            </div>
                        </div>
                        <Button
                            variant={'secondary'}
                            size={'form'} 
                            className='normal-case text-base text-white'
                            onClick={handleClose}
                        >
                            Chiudi
                        </Button>
                    </div>
                );
        }
    };

    return (
        <div className={`w-full bg-white border border-solid ${currentStep === 'summary' ? 'border-primary' : 'border-border'} rounded-lg transition-all duration-300 ease-in-out p-4 relative shadow-lg`}>
            {isExpanded && (
                <button
                    onClick={() => {
                        toggleExpand();
                        setShowComuneSelection(false);
                    }}
                    className="absolute -top-3 -right-1 w-6 h-6 bg-white border border-solid border-border rounded-full flex items-center justify-center shadow-sm hover:bg-gray-50 transition-colors"
                >
                    <span className="text-sm">×</span>
                </button>
            )}
            <div className="max-w-[1200px] mx-auto flex flex-col gap-4">
                {!showComuneSelection && (
                    <div
                        className="flex justify-between items-center cursor-pointer"
                        onClick={toggleExpand}
                    >
                        <div className={`text-base font-semibold ${(!isExpanded && !requestSent) ? 'text-holifya-blue' : 'text-black'}`}>
                            {requestSent ? 'Richiesta inviata' : 'Non sai dove effettuare i tuoi esami del sangue?'}
                        </div>
                    </div>
                )}
                {isExpanded && !showComuneSelection && !requestSent && (
                    <>
                        <div className="text-sm">
                            <strong>Abbiamo noi la soluzione per te!</strong> Richiedi uno slot prelievo presso una delle nostre cliniche convenzionate!
                        </div>
                        <Button size={'form'} className='normal-case text-base' onClick={handleRequestClick}>
                            Richiedi disponibilità
                        </Button>
                    </>
                )}
                {isExpanded && !showComuneSelection && requestSent && renderBookingOverview()}
                {isExpanded && showComuneSelection && renderStep()}
            </div>
        </div>
    );
};

export default BloodTestBanner; 